import {Spinner, css, view} from '@sail/ui/deprecated';
import {useCallback, useEffect, useRef} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {send} from '@sail/icons/react/Icon';

import {CloseButton} from '../CloseButton';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {ChannelType} from '../../ChannelSelector/Channel';
import {useEligibleChannels} from '../../ChannelSelector/useEligibleChannels';
import {ChannelSelectorV2} from '../../ChannelSelector/V2';
import {
  ConnectPlatformSharingNotice,
  useConnectPlatformTransparency,
} from '../../ConnectPlatformTransparency';
import {OptionButton} from '../../ChannelSelector/V2/OptionButton';
import {useProfile} from '../../../Providers/ProfileProvider';

export const messages = defineMessages({
  formTitle: {
    id: 'support_conversations_client.supportWidget.letsChangeThat.title',
    description: 'Title shown in the form',
    defaultMessage: "Let's change that",
  },
  description: {
    id: 'support_conversations_client.supportWidget.letsChangeThat.description',
    description: 'Describes what user can do on this screen',
    defaultMessage:
      'You can talk with a support agent by choosing a contact method, or provide feedback on your experience.',
  },
  descriptionForLoggedOut: {
    id: 'support_conversations_client.supportWidget.letsChangeThat.descriptionForLoggedOut',
    description: 'Describes what logged out user can do on this screen',
    defaultMessage:
      'You can email a support agent or provide feedback on your experience. Live support is only available to signed in users.',
  },
  leaveFeedbackTitle: {
    id: 'support_conversations_client.supportWidget.letsChangeThat.leaveFeedbackTitle',
    description: 'Main text in leave feedback button',
    defaultMessage: 'Leave feedback',
  },
  leaveFeedbackDescription: {
    id: 'support_conversations_client.supportWidget.letsChangeThat.leaveFeedbackDescription',
    description: 'Secondary text in leave feedback button',
    defaultMessage: 'Help us improve this experience',
  },
});

export type LetsChangeThatOption =
  | 'chat'
  | 'request_a_call'
  | 'email'
  | 'feedback';

type Props = {
  onClose: () => void;
  onOptionSelected: (channel: LetsChangeThatOption) => void;
};

export function LetsChangeThat({onClose, onOptionSelected}: Props) {
  const intl = useIntl();
  const analytics = useClientAnalytics();
  const isFirstRenderRef = useRef<boolean>(false);
  const {profile} = useProfile();
  const {user} = profile;

  const {eligibleChannels, noticeMessage, pending} = useEligibleChannels();
  const {platformName, showSharingNotice} = useConnectPlatformTransparency();

  const handleCancel = useCallback(() => {
    analytics.action('support_conversations_lets_change_that_cancel');
    onClose();
  }, [analytics, onClose]);

  const handleOptionSelected = useCallback(
    (option: LetsChangeThatOption) => {
      analytics.action(
        'support_conversations_lets_change_that_option_selected',
        {
          lets_change_that_option: option,
        },
      );
      onOptionSelected(option);
    },
    [analytics, onOptionSelected],
  );

  useEffect(() => {
    if (!isFirstRenderRef.current) {
      isFirstRenderRef.current = true;
      analytics.track('support_conversations_lets_change_that_impression');
    }
  }, [analytics]);

  const isLoggedInUser = !!(
    user?.sessionType && user?.sessionType !== 'anonymous'
  );

  return (
    <view.div
      data-testid="support-widget-lets-change-that"
      css={{position: css.value('relative')}}
    >
      <view.form onSubmit={(e) => e.preventDefault()}>
        <view.h2 css={{margin: 0, paddingTop: 400, marginRight: 'medium'}}>
          {intl.formatMessage(messages.formTitle)}
        </view.h2>

        <view.p css={{marginBottom: 'large'}}>
          {intl.formatMessage(
            isLoggedInUser
              ? messages.description
              : messages.descriptionForLoggedOut,
          )}
        </view.p>

        {pending ? (
          <view.div
            data-testid="loading-spinner"
            css={{
              width: 'fill',
              marginTop: 'medium',
              height: css.value('264px'),
              stack: 'y',
              alignX: 'center',
              alignY: 'center',
            }}
          >
            <Spinner />
          </view.div>
        ) : (
          <>
            <ChannelSelectorV2
              eligibleChannels={eligibleChannels}
              noticeMessage={noticeMessage}
              selectedTopic={'other'}
              onChannelSelected={(channel: ChannelType) =>
                handleOptionSelected(channel.type)
              }
              showUnavailableChannels={false}
            />
            <OptionButton
              title={intl.formatMessage(messages.leaveFeedbackTitle)}
              dataTestId="leave-feedback-button"
              icon={send}
              disabled={false}
              description={intl.formatMessage(
                messages.leaveFeedbackDescription,
              )}
              onClick={() => handleOptionSelected('feedback')}
              isLast
            />
          </>
        )}
        {showSharingNotice && (
          <view.div css={{bleedX: 'medium', marginTop: 'large'}}>
            <ConnectPlatformSharingNotice platformNickname={platformName} />
          </view.div>
        )}
        <CloseButton
          onClick={handleCancel}
          dataTestId="support-widget-lets-change-that-cancel-button"
        />
      </view.form>
    </view.div>
  );
}
